/**
 * Club Navigation Context Hook
 *
 * Resolves club context for navigation with memory across non-club pages.
 * Issue #724: Context-Aware Home Navigation
 * Issue #809: Validate stored slug exists (prevents 404 after club deletion)
 *
 * Strategy:
 * 1. Detect current club from URL (/clubs/:slug/*)
 * 2. Store last viewed club in sessionStorage
 * 3. Use stored club when on non-club pages (ONLY if still valid)
 * 4. Fall back to primary club if no stored context
 *
 * Related: ADR-0005 Navigation Shell & Menu Architecture
 */

import { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { usePrimaryClub } from './usePrimaryClub';
import { useClubs } from './useClubs';

const STORAGE_KEY = 'oromove:lastClubSlug';

/**
 * Get club slug for navigation with context memory
 * 
 * Returns club slug in priority order:
 * 1. Current club from URL (if on /clubs/:slug/* route)
 * 2. Last viewed club from sessionStorage (if navigating from non-club page)
 * 3. Primary club (fallback)
 * 4. null (user has no clubs - edge case)
 */
export function useClubNavContext(): string | null {
  const location = useLocation();
  const { data: clubs } = useClubs();
  const primaryClubSlug = usePrimaryClub();

  // Detect current club from URL
  const match = matchPath('/clubs/:slug/*', location.pathname);
  const currentClubSlug = match?.params?.slug;

  // Store current club when on club pages
  useEffect(() => {
    if (currentClubSlug) {
      try {
        sessionStorage.setItem(STORAGE_KEY, currentClubSlug);
      } catch {
        // Graceful degradation for privacy modes or React Native
        // Context memory won't work, but navigation still functions
      }
    }
  }, [currentClubSlug]);

  // Resolution priority (Issue #724, #809)
  if (currentClubSlug) {
    // On club page → use current club
    return currentClubSlug;
  }

  // On non-club page → try stored club first, but VALIDATE it exists (#809)
  try {
    const storedClubSlug = sessionStorage.getItem(STORAGE_KEY);
    if (storedClubSlug) {
      // Validate stored slug exists in user's current clubs
      if (clubs?.some((c) => c.slug === storedClubSlug)) {
        return storedClubSlug;
      }
      // Clear stale slug if clubs loaded and slug not found
      if (clubs && !clubs.some((c) => c.slug === storedClubSlug)) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  } catch {
    // sessionStorage unavailable (React Native, privacy mode, etc.)
    // Fall through to primary club
  }

  // No valid stored context → fall back to primary club
  return primaryClubSlug;
}
